import {
  CarOwnershipCostWidget,
  DowntimeWidget,
  DriverRatingWidget,
  FleetMileageWidget,
  FleetTableWidget,
  FuelUsageWidget,
  FinanceWidget,
  FleetLoadWidget,
  IncidentsWidget,
  KnowledgeBaseWidget,
  KpiWidget,
  ManagerEfficiencyWidget,
  OpsControlWidget,
  PmComplianceWidget,
  RegionsSummaryWidget,
  RepairBreakdownWidget,
  RemindersWidget,
  ServiceCalendarWidget,
  SupportTicketsWidget,
  TeamTasksWidget,
  TripsActivityWidget,
} from './widgets/index'

/**
 * Тип гаджета → компонент.
 *
 * Заголовки, размеры и настройки по умолчанию приходят с сервера
 * (/admin/dashboards/catalog): каталог там же, где тумблеры владельца,
 * иначе два списка типов начнут расходиться.
 *
 * Здесь только то, что сервер знать не должен: чем рисовать,
 * нужен ли гаджету внутренний скролл и какие настройки он умеет.
 */
export const WIDGET_COMPONENTS = {
  kpi: KpiWidget,
  knowledge_base: KnowledgeBaseWidget,
  fleet_load: FleetLoadWidget,
  fleet_table: FleetTableWidget,
  ops_control: OpsControlWidget,
  manager_efficiency: ManagerEfficiencyWidget,
  incidents: IncidentsWidget,
  reminders: RemindersWidget,
  finance: FinanceWidget,
  trips_activity: TripsActivityWidget,
  driver_rating: DriverRatingWidget,
  team_tasks: TeamTasksWidget,
  support_tickets: SupportTicketsWidget,
  regions_summary: RegionsSummaryWidget,
  fleet_mileage: FleetMileageWidget,
  service_calendar: ServiceCalendarWidget,
  pm_compliance: PmComplianceWidget,
  downtime: DowntimeWidget,
  fuel_usage: FuelUsageWidget,
  car_ownership_cost: CarOwnershipCostWidget,
  repair_breakdown: RepairBreakdownWidget,
}

/** Гаджеты-списки скроллятся внутри, иначе один длинный список рвёт сетку. */
export const SCROLLABLE = new Set([
  'fleet_table', 'ops_control', 'incidents', 'reminders', 'driver_rating', 'knowledge_base',
  'team_tasks', 'support_tickets', 'regions_summary', 'service_calendar',
  'pm_compliance', 'fuel_usage', 'car_ownership_cost', 'repair_breakdown',
])

export const SIZE_LABELS = { s: 'S', m: 'M', l: 'L' }

/** Какие настройки показывать в модалке гаджета. */
export const SETTINGS_SCHEMA = {
  kpi: [
    {
      key: 'metric',
      label: 'Показатель',
      options: [
        { value: 'utilization', label: 'Утилизация парка' },
        { value: 'cost_per_km', label: 'Себестоимость километра' },
        { value: 'line_rate', label: 'Выход на линию' },
        { value: 'active_trips', label: 'Активные рейсы' },
        { value: 'pending_trips', label: 'Ждут подтверждения' },
        { value: 'maintenance_cars', label: 'Машины в ремонте' },
        { value: 'urgent_tickets', label: 'Срочные тикеты' },
        { value: 'pending_users', label: 'Заявки водителей' },
        { value: 'overdue_tasks', label: 'Просроченные задачи' },
      ],
    },
  ],
  manager_efficiency: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  finance: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  trips_activity: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  team_tasks: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  regions_summary: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  fleet_mileage: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  fuel_usage: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  car_ownership_cost: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  repair_breakdown: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  downtime: [
    {
      key: 'period',
      label: 'Период',
      options: [
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
      ],
    },
  ],
  service_calendar: [
    {
      key: 'days',
      label: 'Горизонт',
      options: [
        { value: 30, label: '30 дней' },
        { value: 60, label: '60 дней' },
        { value: 90, label: '90 дней' },
      ],
    },
  ],
  reminders: [
    {
      key: 'days',
      label: 'Горизонт',
      options: [
        { value: 14, label: '14 дней' },
        { value: 30, label: '30 дней' },
        { value: 90, label: '90 дней' },
      ],
    },
  ],
}
